import { useCallback, useReducer } from "react";
import { getAssetTypeKey } from "../shared/constants/managedSurveyAssetTypes";
import { IdName } from "../shared/interfaces/Asset";
import { Customer } from "../shared/interfaces/Customer";
import {
  PanelMeasurement,
  Result,
  ResultResubmission,
  ResultResubmissionPanel,
  ResultResubmissionSwitchgear,
  ResultSurveyableAsset,
} from "../shared/interfaces/Result";
import { Panel } from "../shared/interfaces/Switchgear";

export enum Step {
  SelectCustomer = "customer.selectCustomer",
  SelectSubstation = "substations.selectSubstation",
  SelectSwitchgear = "asset.selectAsset",
  SelectPanels = "panels.checkPanels",
}

interface ActiveStep {
  step: Step;
  index: number;
}

export interface PartialResultPanel extends Panel {
  isExcluded: boolean;
  parentAssetId?: number;
}

export interface PartialResultSwitchgear {
  assetId: number;
  name: string;
  assetType: string;
  panels?: PartialResultPanel[];
}

const mapPartialSwitchgearToSubmissionSwitchgear = (
  switchgear: PartialResultSwitchgear,
): ResultResubmissionSwitchgear => ({
  switchGearId: switchgear.assetId,
  assetType: switchgear.assetType,
  panels:
    switchgear.panels?.map(
      (panel: PartialResultPanel) =>
        ({
          panelNo: panel.panelNo,
          panel360AssetId: panel.panelAssetId || 0,
          isExcluded: panel.isExcluded,
          panelName: panel.name,
        }) as ResultResubmissionPanel,
    ) || [],
});

export interface PartialResultReducer {
  state: PartialResultState;
  setResult: (result: Result) => void;
  incrementStep: () => void;
  decrementStep: () => void;
  setCustomer: (customer?: Customer) => void;
  setSubstation: (id?: number, name?: string) => void;
  setSwitchgear: (id?: number, name?: string) => void;
  setSwitchgears: (switchgears?: PartialResultSwitchgear[]) => void;
  updatePanelAssetIds: (panelAssets: Panel[] | undefined) => void;
  updateGroupPanelAssetIds: (panelAssets: Panel[] | undefined, parentAssetId?: number) => void;
  updateAllSwitchgearsPanelAssetIds: (panelAssets: PartialResultPanel[] | undefined) => void;
  updatePanelAssetId: (panelNo: string, panelAssetId: number) => void;
  updateGroupPanelAssetId: (panelNo: string, panelAssetId: number, parentAssetId?: number) => void;
  checkPanelMatch: (panelNo: string, parentAssetId?: number, originalPanels?: Panel[]) => boolean | undefined;
  getPartialResultPanel: (panelNo: string, parentAssetId?: number) => PartialResultPanel | undefined;
  getResubmissionObject: () => ResultResubmission | undefined;
  setPanelExcludedStatus: (panelNo: string, excluded: boolean) => void;
  setGroupPanelExcludedStatus: (panelNo: string, excluded: boolean, parentAssetId?: number) => void;
}

interface PartialResultState {
  steps: Step[];
  activeStep: ActiveStep;
  isLastStep: boolean;
  stepIsValid: boolean;
  nextStepButtonEnabled: boolean;
  selectedCustomer?: Customer;
  selectedSubstation?: IdName;
  selectedSwitchgear?: IdName;
  selectedSwitchgears?: PartialResultSwitchgear[];
  panels: PartialResultPanel[];
}

type PartialResultAction =
  | { type: "setResult"; result: Result; includeCustomerStep: boolean }
  | { type: "incrementStep" }
  | { type: "decrementStep" }
  | { type: "setCustomer"; customer?: Customer }
  | { type: "setSubstation"; idName?: IdName }
  | { type: "setSwitchgear"; idName?: IdName }
  | { type: "setSwitchgears"; switchgears?: PartialResultSwitchgear[] }
  | { type: "setPanelAssetIds"; panelAssets?: Panel[] }
  | {
      type: "setSwitchgearPanelAssetIds";
      panelAssets?: Panel[];
      parentAssetId?: number;
    }
  | { type: "setAllSwitchgearsPanelAssetIds"; panelAssets?: PartialResultPanel[] }
  | { type: "updatePanelAssetId"; panelNo: string; panelAssetId: number }
  | {
      type: "updateGroupPanelAssetId";
      panelNo: string;
      panelAssetId: number;
      parentAssetId?: number;
    }
  | { type: "setPanelExcludedStatus"; panelNo: string; excluded: boolean }
  | {
      type: "setGroupPanelExcludedStatus";
      panelNo: string;
      excluded: boolean;
      parentAssetId?: number;
    };

function findPanelAsset(assets: Panel[] | undefined, panelNo: string) {
  return assets?.find((panel: Panel) => panel.panelNo === panelNo);
}

function getStepValidStatus(activeStep: ActiveStep, state: PartialResultState) {
  switch (activeStep.step) {
    case Step.SelectCustomer:
      return !!state.selectedCustomer;
    case Step.SelectSubstation:
      return !!state.selectedSubstation;
    case Step.SelectSwitchgear:
      if (!!state.selectedSwitchgear) {
        return true;
      }
      if (!!state.selectedSwitchgears) {
        const uniqueIds = new Set(
          state.selectedSwitchgears.map((switchgear: PartialResultSwitchgear) => switchgear.assetId),
        );
        if (
          uniqueIds.size < state.selectedSwitchgears.length ||
          state.selectedSwitchgears.some((switchgear: PartialResultSwitchgear) => switchgear.assetId === 0)
        ) {
          return false;
        }
      }
      return (state.selectedSwitchgears?.length ?? 0) > 0;
    case Step.SelectPanels:
      if (!state.selectedSwitchgears) {
        return !state.panels.some((panel) => !panel.panelAssetId && !panel.isExcluded);
      }

      const panels =
        state.selectedSwitchgears
          .flatMap((switchgear: PartialResultSwitchgear) => switchgear.panels)
          .filter((panel) => panel != null) || [];
      return !panels.some((panel) => panel?.panelAssetId == null && !panel?.isExcluded);
  }
}

function partialResultReducer(state: PartialResultState, action: PartialResultAction): PartialResultState {
  let updatedState = { ...state };
  const panels = [...state.panels];
  const switchgears = [...(state.selectedSwitchgears ?? [])];
  let foundPanel: PartialResultPanel | undefined;

  switch (action.type) {
    case "setResult":
      updatedState = generateInitialPartialResultState(action.result, action.includeCustomerStep);
      break;
    case "incrementStep":
      const incrementedStepIndex = state.activeStep.index + 1;

      if (state.stepIsValid)
        updatedState = {
          ...state,
          activeStep: {
            step: state.steps[incrementedStepIndex],
            index: incrementedStepIndex,
          },
          isLastStep: incrementedStepIndex === state.steps.length - 1,
        };
      break;
    case "decrementStep":
      const decrementedStepIndex = state.activeStep.index - 1;
      updatedState = {
        ...state,
        activeStep: {
          step: state.steps[decrementedStepIndex],
          index: decrementedStepIndex,
        },
        isLastStep: false,
      };
      break;
    case "setCustomer":
      updatedState = {
        ...state,
        selectedCustomer: action.customer,
        selectedSubstation: undefined,
        selectedSwitchgear: undefined,
      };
      break;
    case "setSubstation":
      updatedState = {
        ...state,
        selectedSubstation: action.idName,
        selectedSwitchgear: undefined,
      };
      break;
    case "setSwitchgear":
      updatedState = {
        ...state,
        selectedSwitchgear: action.idName,
      };
      break;
    case "setSwitchgears":
      updatedState = {
        ...state,
        selectedSwitchgears: action.switchgears,
      };
      break;
    case "setPanelAssetIds":
      panels.forEach((panel) => {
        const panelAsset = findPanelAsset(action.panelAssets, panel.panelNo);
        panel.panelAssetId = panelAsset?.panelAssetId;
      });

      updatedState = {
        ...state,
        panels: panels,
      };
      break;
    case "setSwitchgearPanelAssetIds":
      switchgears
        ?.find((switchgear: PartialResultSwitchgear) => switchgear.assetId === action.parentAssetId)
        ?.panels?.forEach((panel: PartialResultPanel) => {
          const panelAsset = findPanelAsset(action.panelAssets, panel.panelNo);
          panel.panelAssetId = panelAsset?.panelAssetId;
        });

      updatedState = {
        ...state,
        selectedSwitchgears: switchgears,
      };
      break;

    case "setAllSwitchgearsPanelAssetIds":
      if (!action.panelAssets) break;
      action.panelAssets.forEach((panel) => {
        const switchgear = switchgears.find((s) => s.assetId === panel.parentAssetId);

        if (switchgear) {
          const matchingPanel = findPanelAsset(switchgear.panels, panel.panelNo);

          if (matchingPanel) {
            matchingPanel.panelAssetId = panel.panelAssetId;
          }
        }
      });

      updatedState = {
        ...state,
        selectedSwitchgears: switchgears,
      };
      break;

    case "updatePanelAssetId":
      foundPanel = panels.find((panel: PartialResultPanel) => panel.panelNo === action.panelNo);
      if (foundPanel) foundPanel.panelAssetId = action.panelAssetId;

      updatedState = {
        ...state,
        panels: panels,
      };
      break;
    case "updateGroupPanelAssetId":
      foundPanel = switchgears
        .find((switchgear: PartialResultSwitchgear) => switchgear.assetId === action.parentAssetId)
        ?.panels?.find((panel: PartialResultPanel) => panel.panelNo === action.panelNo);
      if (foundPanel) foundPanel.panelAssetId = action.panelAssetId;

      updatedState = {
        ...state,
        selectedSwitchgears: switchgears,
      };
      break;
    case "setPanelExcludedStatus":
      foundPanel = panels.find((panel: PartialResultPanel) => panel.panelNo === action.panelNo);
      if (foundPanel) foundPanel.isExcluded = action.excluded;

      updatedState = {
        ...state,
        panels: panels,
      };
      break;
    case "setGroupPanelExcludedStatus":
      foundPanel = switchgears
        .find((switchgear: PartialResultSwitchgear) => switchgear.assetId === action.parentAssetId)
        ?.panels?.find((panel: PartialResultPanel) => panel.panelNo === action.panelNo);
      if (foundPanel) foundPanel.isExcluded = action.excluded;

      updatedState = {
        ...state,
        selectedSwitchgears: switchgears,
      };
      break;
  }

  return {
    ...updatedState,
    stepIsValid: getStepValidStatus(updatedState.activeStep, updatedState),
  };
}

function generateInitialPartialResultState(
  result: Result | undefined,
  includeCustomerStep: boolean,
): PartialResultState {
  const steps = includeCustomerStep
    ? [Step.SelectCustomer, Step.SelectSubstation, Step.SelectSwitchgear, Step.SelectPanels]
    : [Step.SelectSubstation, Step.SelectSwitchgear, Step.SelectPanels];

  return {
    steps: steps,
    activeStep: { step: steps[0], index: 0 },
    isLastStep: false,
    nextStepButtonEnabled: false,
    selectedSubstation: undefined,
    selectedSwitchgear: undefined,
    selectedSwitchgears:
      result?.dataValue?.Assets?.map((asset: ResultSurveyableAsset) => ({
        assetId: asset.AssetId,
        name: asset.AssetName,
        assetType: getAssetTypeKey(asset.AssetType),
        panels: asset.PanelMeasurements.map((panelMeasurement: PanelMeasurement) => ({
          panelNo: panelMeasurement.PanelNo,
          name: panelMeasurement.PanelName,
          panelAssetId: undefined,
          isExcluded: false,
        })),
      })) ?? [],
    stepIsValid: false,
    panels:
      result?.dataValue?.PanelMeasurements?.map((panelMeasurement: PanelMeasurement) => ({
        panelNo: panelMeasurement.PanelNo,
        name: panelMeasurement.PanelName,
        panelAssetId: undefined,
        isExcluded: false,
      })) || [],
  };
}

export function usePartialResultReducer(result?: Result): PartialResultReducer {
  //TODO - as part of task #8986 need to find a way to determine if user only has access to one customer - if so, auto-select it and don't display the 'select a customer' step
  const includeCustomerStep = true;

  const reducer = (state: PartialResultState, action: PartialResultAction) => partialResultReducer(state, action);
  const [state, updatePartialResultDetails] = useReducer(
    reducer,
    generateInitialPartialResultState(result, includeCustomerStep),
  );

  const setResult = useCallback(
    (updatedResult: Result) => {
      updatePartialResultDetails({
        type: "setResult",
        result: updatedResult,
        includeCustomerStep: includeCustomerStep,
      });
    },
    [includeCustomerStep],
  );

  const incrementStep = useCallback(() => {
    updatePartialResultDetails({ type: "incrementStep" });
  }, []);

  const decrementStep = useCallback(() => {
    updatePartialResultDetails({ type: "decrementStep" });
  }, []);

  const setCustomer = useCallback((customer?: Customer) => {
    updatePartialResultDetails({ type: "setCustomer", customer: customer });
  }, []);

  const setSubstation = useCallback((id?: number, name?: string) => {
    updatePartialResultDetails({
      type: "setSubstation",
      idName: id && name ? { id: id, name: name } : undefined,
    });
  }, []);

  const setSwitchgear = useCallback((id?: number, name?: string) => {
    updatePartialResultDetails({
      type: "setSwitchgear",
      idName: id && name ? { id: id, name: name } : undefined,
    });
  }, []);

  const setSwitchgears = useCallback((switchgears?: PartialResultSwitchgear[]) => {
    updatePartialResultDetails({
      type: "setSwitchgears",
      switchgears: switchgears,
    });
  }, []);

  const updatePanelAssetIds = useCallback((panelAssets: Panel[] | undefined) => {
    updatePartialResultDetails({
      type: "setPanelAssetIds",
      panelAssets: panelAssets,
    });
  }, []);

  const updateSwitchgearPanelAssetIds = useCallback((panelAssets: Panel[] | undefined, parentAssetId?: number) => {
    updatePartialResultDetails({
      type: "setSwitchgearPanelAssetIds",
      panelAssets: panelAssets,
      parentAssetId: parentAssetId,
    });
  }, []);

  const updateAllSwitchgearsPanelAssetIds = useCallback((panelAssets: PartialResultPanel[] | undefined) => {
    updatePartialResultDetails({
      type: "setAllSwitchgearsPanelAssetIds",
      panelAssets: panelAssets,
    });
  }, []);

  const updatePanelAssetId = useCallback((panelNo: string, panelAssetId: number) => {
    updatePartialResultDetails({
      type: "updatePanelAssetId",
      panelNo: panelNo,
      panelAssetId: panelAssetId,
    });
  }, []);

  const updateGroupPanelAssetId = useCallback((panelNo: string, panelAssetId: number, parentAssetId?: number) => {
    updatePartialResultDetails({
      type: "updateGroupPanelAssetId",
      panelNo: panelNo,
      panelAssetId: panelAssetId,
      parentAssetId: parentAssetId,
    });
  }, []);

  const setPanelExcludedStatus = useCallback((panelNo: string, excluded: boolean) => {
    updatePartialResultDetails({
      type: "setPanelExcludedStatus",
      panelNo: panelNo,
      excluded: excluded,
    });
  }, []);

  const setGroupPanelExcludedStatus = useCallback((panelNo: string, excluded: boolean, parentAssetId?: number) => {
    updatePartialResultDetails({
      type: "setGroupPanelExcludedStatus",
      panelNo: panelNo,
      excluded: excluded,
      parentAssetId: parentAssetId,
    });
  }, []);

  const checkPanelMatch = useCallback(
    (panelNo: string, parentAssetId?: number, originalPanels?: Panel[]) => {
      // If there is no parent asset it is the version 1 style of partial results.
      if (!parentAssetId) {
        return !!state.panels.find((panel) => panel.panelNo === panelNo)?.panelAssetId;
      }

      // If there is a parent asset, it is the version 2 style of partial results.
      // Check if the panel is in the original switchgear panels list.
      return !!originalPanels?.find((panel) => panel.panelNo === panelNo);
    },
    [state.panels],
  );

  const getPartialResultPanel = useCallback(
    (panelNo: string, parentAssetId?: number) => {
      if (!parentAssetId) {
        return state.panels.find((panel) => panel.panelNo === panelNo);
      }
      return state.selectedSwitchgears
        ?.find((switchgear: PartialResultSwitchgear) => switchgear.assetId === parentAssetId)
        ?.panels?.find((panel: PartialResultPanel) => panel.panelNo === panelNo);
    },
    [state.panels, state.selectedSwitchgears],
  );

  const getResubmissionObject = useCallback((): ResultResubmission | undefined => {
    if (result && state.selectedSubstation && state.selectedSwitchgear) {
      return {
        hvSubstationId: state.selectedSubstation.id,
        switchGearId: state.selectedSwitchgear.id,
        captureId: result?.captureId,
        panelNos: state.panels
          .filter((panel: PartialResultPanel) => !panel.isExcluded)
          .map((panel: PartialResultPanel) => panel.panelNo),
        excludedPanelNos: state.panels
          .filter((panel: PartialResultPanel) => panel.isExcluded)
          .map((panel: PartialResultPanel) => panel.panelNo),
        originalFileName: result.dataValue.OriginalFileName,
        savedFileName: result.dataValue.SavedFileName,
      };
    } else if (
      result &&
      state.selectedSubstation &&
      state.selectedSwitchgears?.every((switchgear: PartialResultSwitchgear) =>
        switchgear.panels?.every((panel: PartialResultPanel) => panel.panelAssetId || panel.isExcluded),
      )
    ) {
      return {
        hvSubstationId: state.selectedSubstation.id,
        switchGearId: 0,
        captureId: result?.captureId,
        panelNos: state.selectedSwitchgears.flatMap(
          (switchgear: PartialResultSwitchgear) =>
            switchgear.panels
              ?.filter((panel: PartialResultPanel) => !panel.isExcluded)
              .map((panel: PartialResultPanel) => panel.panelNo) || [],
        ),
        excludedPanelNos: state.selectedSwitchgears.flatMap(
          (switchgear: PartialResultSwitchgear) =>
            switchgear.panels
              ?.filter((panel: PartialResultPanel) => panel.isExcluded)
              .map((panel: PartialResultPanel) => panel.panelNo) || [],
        ),
        panelAssetIds: state.selectedSwitchgears.flatMap(
          (switchgear: PartialResultSwitchgear) =>
            switchgear.panels
              ?.filter((panel: PartialResultPanel) => !panel.isExcluded)
              .map((panel: PartialResultPanel) => panel.panelAssetId ?? 0) || [],
        ),
        excludedPanelAssetIds: state.selectedSwitchgears.flatMap(
          (switchgear: PartialResultSwitchgear) =>
            switchgear.panels
              ?.filter((panel: PartialResultPanel) => panel.isExcluded)
              .map((panel: PartialResultPanel) => panel.panelAssetId ?? 0) || [],
        ),
        originalFileName: result.dataValue.OriginalFileName,
        savedFileName: result.dataValue.SavedFileName,
        switchGears: state.selectedSwitchgears.map(mapPartialSwitchgearToSubmissionSwitchgear),
      };
    } else return undefined;
  }, [result, state.selectedSubstation, state.selectedSwitchgear, state.selectedSwitchgears, state.panels]);

  return {
    state,
    setResult,
    incrementStep,
    decrementStep,
    setCustomer,
    setSubstation,
    setSwitchgear,
    setSwitchgears,
    updatePanelAssetIds,
    updateGroupPanelAssetIds: updateSwitchgearPanelAssetIds,
    updateAllSwitchgearsPanelAssetIds,
    updatePanelAssetId,
    updateGroupPanelAssetId,
    setPanelExcludedStatus,
    setGroupPanelExcludedStatus,
    checkPanelMatch,
    getPartialResultPanel,
    getResubmissionObject,
  };
}
